import { useEffect, useMemo, useRef, useState } from "react";
import { formatearPrecio } from "../../../utils/moneda";
import "./ShippingFormModal.css";

const TAB_KEYS = {
  GENERAL: "general",
  DELIVERY: "delivery",
  RULES: "rules",
  PREVIEW: "preview",
};

const EMPTY_FORM = {
  estado: 1,
  nombre: "",
  nombre_visible: "",
  descripcion: "",
  costo_base: "",
  tiempo_entrega_desde: "",
  tiempo_entrega_hasta: "",
  unidad_tiempo: "DIAS",
  requiere_cuenta: 0,
  label_cuenta: "",
  label_empresa: "",
};

function normalizeMetodoData(data) {
  if (!data) return { ...EMPTY_FORM };

  return {
    estado: Number(data.estado ?? 1) === 1 ? 1 : 0,
    nombre: data.nombre ?? "",
    nombre_visible: data.nombre_visible ?? "",
    descripcion: data.descripcion ?? "",
    costo_base:
      data.costo_base !== undefined && data.costo_base !== null
        ? String(data.costo_base)
        : "",
    tiempo_entrega_desde:
      data.tiempo_entrega_desde !== undefined &&
      data.tiempo_entrega_desde !== null
        ? String(data.tiempo_entrega_desde)
        : "",
    tiempo_entrega_hasta:
      data.tiempo_entrega_hasta !== undefined &&
      data.tiempo_entrega_hasta !== null
        ? String(data.tiempo_entrega_hasta)
        : "",
    unidad_tiempo: (data.unidad_tiempo || "DIAS").toUpperCase(),
    requiere_cuenta: Number(data.requiere_cuenta ?? 0) === 1 ? 1 : 0,
    label_cuenta: data.label_cuenta ?? "",
    label_empresa: data.label_empresa ?? "",
  };
}

function buildPreviewRange(form) {
  const from = Number(form.tiempo_entrega_desde);
  const to = Number(form.tiempo_entrega_hasta);

  if (!Number.isFinite(from) || !Number.isFinite(to)) return "Sin definir";

  const unit =
    form.unidad_tiempo === "HORAS"
      ? from === 1 && to === 1
        ? "hora"
        : "horas"
      : from === 1 && to === 1
      ? "día"
      : "días";

  if (from === to) return `${from} ${unit}`;
  return `${from} a ${to} ${unit}`;
}

function ShippingFormModal({
  isOpen,
  onClose,
  metodoData,
  configuracionTienda,
  onSubmit,
  onManageRules,
  onEditRule,
  onDeleteRule,
}) {
  const isEdit = !!metodoData;
  const [activeTab, setActiveTab] = useState(TAB_KEYS.GENERAL);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [alert, setAlert] = useState(null);
  const [saving, setSaving] = useState(false);
  const alertTimerRef = useRef(null);

  const rules = Array.isArray(metodoData?.reglas) ? metodoData.reglas : [];
  const hasMethodId = !!metodoData?.tienda_metodo_envio_id;

  const showAlert = (type, message) => {
    setAlert({ type, message: String(message || "") });

    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 4500);
  };

  useEffect(() => {
    if (!isOpen) return;

    setForm(normalizeMetodoData(metodoData));
    setAlert(null);
    setSaving(false);
    setActiveTab(TAB_KEYS.GENERAL);

    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, [isOpen, metodoData]);

  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose?.();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose, saving]);

  const preview = useMemo(() => {
    return {
      estado: Number(form.estado) === 1 ? "Activo" : "Inactivo",
      nombreVisible: form.nombre_visible?.trim() || "Sin nombre visible",
      nombreInterno: form.nombre?.trim() || "Sin nombre interno",
      descripcion: form.descripcion?.trim() || "Sin descripción",
      costo:
        formatearPrecio(form.costo_base, configuracionTienda, {
          espacioEntreSimbolo: true,
        }) || "—",
      entrega: buildPreviewRange(form),
      requiereCuenta: Number(form.requiere_cuenta) === 1,
      labelCuenta: form.label_cuenta?.trim() || "Cuenta",
      labelEmpresa: form.label_empresa?.trim() || "Empresa",
    };
  }, [form, configuracionTienda]);

  const updateField = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => {
      if (type === "checkbox") {
        return { ...prev, [name]: checked ? 1 : 0 };
      }

      return { ...prev, [name]: value };
    });
  };

  const setTab = (tab) => {
    if (tab === TAB_KEYS.RULES && !hasMethodId) {
      showAlert(
        "info",
        "Guarda primero el método para poder configurar sus reglas por zona."
      );
      return;
    }
    setActiveTab(tab);
  };

  const validateForm = () => {
    const errors = [];

    const nombre = String(form.nombre || "").trim();
    const nombreVisible = String(form.nombre_visible || "").trim();
    const descripcion = String(form.descripcion || "").trim();
    const labelCuenta = String(form.label_cuenta || "").trim();
    const labelEmpresa = String(form.label_empresa || "").trim();

    const costoBase = Number(form.costo_base);
    const entregaDesde = Number(form.tiempo_entrega_desde);
    const entregaHasta = Number(form.tiempo_entrega_hasta);
    const unidad = String(form.unidad_tiempo || "").toUpperCase();

    if (!nombre) errors.push("El nombre interno es obligatorio.");
    else if (nombre.length < 2)
      errors.push("El nombre interno debe tener al menos 2 caracteres.");
    else if (nombre.length > 100)
      errors.push("El nombre interno no puede exceder 100 caracteres.");

    if (!nombreVisible) errors.push("El nombre visible es obligatorio.");
    else if (nombreVisible.length < 2)
      errors.push("El nombre visible debe tener al menos 2 caracteres.");
    else if (nombreVisible.length > 100)
      errors.push("El nombre visible no puede exceder 100 caracteres.");

    if (descripcion.length > 255) {
      errors.push("La descripción no puede exceder 255 caracteres.");
    }

    if (!Number.isFinite(costoBase) || costoBase < 0) {
      errors.push("El costo base debe ser un número mayor o igual a 0.");
    }

    if (!Number.isInteger(entregaDesde) || entregaDesde < 0) {
      errors.push("El tiempo de entrega inicial debe ser un entero mayor o igual a 0.");
    }

    if (!Number.isInteger(entregaHasta) || entregaHasta < 0) {
      errors.push("El tiempo de entrega final debe ser un entero mayor o igual a 0.");
    }

    if (
      Number.isInteger(entregaDesde) &&
      Number.isInteger(entregaHasta) &&
      entregaHasta < entregaDesde
    ) {
      errors.push("El tiempo de entrega final no puede ser menor al inicial.");
    }

    if (!["HORAS", "DIAS"].includes(unidad)) {
      errors.push("La unidad de tiempo debe ser HORAS o DIAS.");
    }

    if (Number(form.requiere_cuenta) === 1) {
      if (!labelCuenta) errors.push("El label de cuenta es obligatorio.");
      if (!labelEmpresa) errors.push("El label de empresa es obligatorio.");
      if (labelCuenta.length > 100)
        errors.push("El label de cuenta no puede exceder 100 caracteres.");
      if (labelEmpresa.length > 100)
        errors.push("El label de empresa no puede exceder 100 caracteres.");
    }

    return errors;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;

    setAlert(null);

    const errors = validateForm();
    if (errors.length) {
      showAlert("error", errors[0]);
      return;
    }

    setSaving(true);

    try {
      await onSubmit({
        estado: Number(form.estado) === 1 ? 1 : 0,
        nombre: String(form.nombre || "").trim(),
        nombre_visible: String(form.nombre_visible || "").trim(),
        descripcion: String(form.descripcion || "").trim() || null,
        costo_base: Number(form.costo_base || 0),
        tiempo_entrega_desde: Number(form.tiempo_entrega_desde || 0),
        tiempo_entrega_hasta: Number(form.tiempo_entrega_hasta || 0),
        unidad_tiempo: String(form.unidad_tiempo || "DIAS").toUpperCase(),
        requiere_cuenta: Number(form.requiere_cuenta) === 1 ? 1 : 0,
        label_cuenta:
          Number(form.requiere_cuenta) === 1
            ? String(form.label_cuenta || "").trim()
            : null,
        label_empresa:
          Number(form.requiere_cuenta) === 1
            ? String(form.label_empresa || "").trim()
            : null,
      });

      onClose?.();
    } catch (error) {
      console.error("Error al guardar el método de envío:", error);
      showAlert(
        "error",
        error?.message || "No se pudo guardar el método de envío."
      );
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="tienda-modal__overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose?.();
      }}
    >
      <div
        className="tienda-modal tienda-modal--wide tienda-modal--shell"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="tienda-modal__header">
          <div className="tienda-modal__header-text">
            <h2 className="tienda-modal__title">
              {isEdit ? "Editar método de envío" : "Crear método de envío"}
            </h2>
            <p className="tienda-modal__subtitle">
              Configura la información general, tiempos de entrega y reglas por
              zona.
            </p>
          </div>

          <button
            type="button"
            className="tienda-modal__close"
            onClick={onClose}
            disabled={saving}
          >
            ✕
          </button>
        </div>

        <div className="shipform-header-summary">
          <div className="shipform-summary-badge-wrap">
            <span
              className={`shipform-badge ${
                Number(form.estado) === 1 ? "is-success" : "is-muted"
              }`}
            >
              {Number(form.estado) === 1 ? "Activo" : "Inactivo"}
            </span>

            {Number(form.requiere_cuenta) === 1 && (
              <span className="shipform-badge is-info">Requiere cuenta</span>
            )}

            {hasMethodId && (
              <span className="shipform-badge is-light">
                {rules.length} regla{rules.length === 1 ? "" : "s"}
              </span>
            )}
          </div>

          <div className="shipform-summary-mini-grid">
            <div className="shipform-summary-mini-card">
              <span className="shipform-summary-mini-label">Nombre visible</span>
              <strong className="shipform-summary-mini-value">
                {preview.nombreVisible}
              </strong>
            </div>

            <div className="shipform-summary-mini-card">
              <span className="shipform-summary-mini-label">Costo base</span>
              <strong className="shipform-summary-mini-value">{preview.costo}</strong>
            </div>

            <div className="shipform-summary-mini-card">
              <span className="shipform-summary-mini-label">Entrega estimada</span>
              <strong className="shipform-summary-mini-value">{preview.entrega}</strong>
            </div>
          </div>
        </div>

        {alert && (
          <div
            className={`shipform-alert ${
              alert.type === "error" ? "is-error" : "is-info"
            }`}
          >
            <span>{alert.message}</span>
            <button
              className="shipform-alert-x"
              onClick={() => setAlert(null)}
              disabled={saving}
              type="button"
            >
              ✕
            </button>
          </div>
        )}

        <div className="shipform-tabs-bar">
          <button
            type="button"
            className={`shipform-tab-btn ${
              activeTab === TAB_KEYS.GENERAL ? "is-active" : ""
            }`}
            onClick={() => setTab(TAB_KEYS.GENERAL)}
          >
            Información general
          </button>

          <button
            type="button"
            className={`shipform-tab-btn ${
              activeTab === TAB_KEYS.DELIVERY ? "is-active" : ""
            }`}
            onClick={() => setTab(TAB_KEYS.DELIVERY)}
          >
            Costos y entrega
          </button>

          <button
            type="button"
            className={`shipform-tab-btn ${
              activeTab === TAB_KEYS.RULES ? "is-active" : ""
            }${!hasMethodId ? " is-disabled" : ""}`}
            onClick={() => setTab(TAB_KEYS.RULES)}
          >
            Reglas por zona
          </button>

          <button
            type="button"
            className={`shipform-tab-btn ${
              activeTab === TAB_KEYS.PREVIEW ? "is-active" : ""
            }`}
            onClick={() => setTab(TAB_KEYS.PREVIEW)}
          >
            Vista previa
          </button>
        </div>

        <form onSubmit={submit} className="shipform-form">
          <div className="shipform-scroll-body">
            {activeTab === TAB_KEYS.GENERAL && (
              <div className="shipform-panel-grid">
                <section className="shipform-section-card">
                  <div className="shipform-section-header">
                    <h3 className="shipform-section-title">Datos principales</h3>
                    <p className="shipform-section-text">
                      Define el nombre interno y la forma en que se mostrará al
                      cliente.
                    </p>
                  </div>

                  <div className="shipform-grid2">
                    <div className="shipform-input-group">
                      <label className="shipform-label">Nombre interno</label>
                      <input
                        type="text"
                        name="nombre"
                        value={form.nombre}
                        onChange={updateField}
                        className="shipform-input"
                        disabled={saving}
                        placeholder="Ej. cargo_aereo"
                      />
                    </div>

                    <div className="shipform-input-group">
                      <label className="shipform-label">Nombre visible</label>
                      <input
                        type="text"
                        name="nombre_visible"
                        value={form.nombre_visible}
                        onChange={updateField}
                        className="shipform-input"
                        disabled={saving}
                        placeholder="Ej. Cargo aéreo"
                      />
                    </div>
                  </div>

                  <div className="shipform-input-group">
                    <label className="shipform-label">Descripción</label>
                    <textarea
                      name="descripcion"
                      value={form.descripcion}
                      onChange={updateField}
                      className="shipform-textarea"
                      disabled={saving}
                      placeholder="Describe este método para uso administrativo o visual."
                    />
                  </div>

                  <div className="shipform-grid2">
                    <label className="shipform-switch-card">
                      <div>
                        <span className="shipform-switch-title">Método activo</span>
                        <p className="shipform-switch-text">
                          Permite mostrar o desactivar este método.
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        name="estado"
                        checked={Number(form.estado) === 1}
                        onChange={updateField}
                        disabled={saving}
                      />
                    </label>

                    <label className="shipform-switch-card">
                      <div>
                        <span className="shipform-switch-title">Requiere cuenta</span>
                        <p className="shipform-switch-text">
                          Solicita datos adicionales como cuenta y empresa.
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        name="requiere_cuenta"
                        checked={Number(form.requiere_cuenta) === 1}
                        onChange={updateField}
                        disabled={saving}
                      />
                    </label>
                  </div>
                </section>

                <aside className="shipform-side-card">
                  <h3 className="shipform-side-title">Resumen rápido</h3>
                  <div className="shipform-side-info-list">
                    <div className="shipform-side-info-item">
                      <span className="shipform-side-info-label">Estado</span>
                      <strong className="shipform-side-info-value">{preview.estado}</strong>
                    </div>
                    <div className="shipform-side-info-item">
                      <span className="shipform-side-info-label">Nombre visible</span>
                      <strong className="shipform-side-info-value">
                        {preview.nombreVisible}
                      </strong>
                    </div>
                    <div className="shipform-side-info-item">
                      <span className="shipform-side-info-label">Nombre interno</span>
                      <strong className="shipform-side-info-value">
                        {preview.nombreInterno}
                      </strong>
                    </div>
                  </div>
                </aside>

                {Number(form.requiere_cuenta) === 1 && (
                  <section className="shipform-section-card is-full-span">
                    <div className="shipform-section-header">
                      <h3 className="shipform-section-title">Campos adicionales</h3>
                      <p className="shipform-section-text">
                        Estos labels se mostrarán cuando el método requiera
                        cuenta.
                      </p>
                    </div>

                    <div className="shipform-grid2">
                      <div className="shipform-input-group">
                        <label className="shipform-label">Label cuenta</label>
                        <input
                          type="text"
                          name="label_cuenta"
                          value={form.label_cuenta}
                          onChange={updateField}
                          className="shipform-input"
                          disabled={saving}
                          placeholder="Ej. Número de cuenta"
                        />
                      </div>

                      <div className="shipform-input-group">
                        <label className="shipform-label">Label empresa</label>
                        <input
                          type="text"
                          name="label_empresa"
                          value={form.label_empresa}
                          onChange={updateField}
                          className="shipform-input"
                          disabled={saving}
                          placeholder="Ej. Empresa courier"
                        />
                      </div>
                    </div>
                  </section>
                )}
              </div>
            )}

            {activeTab === TAB_KEYS.DELIVERY && (
              <div className="shipform-panel-grid">
                <section className="shipform-section-card">
                  <div className="shipform-section-header">
                    <h3 className="shipform-section-title">Costos y tiempos</h3>
                    <p className="shipform-section-text">
                      Configura el costo base y el rango de tiempo estimado.
                    </p>
                  </div>

                  <div className="shipform-grid3">
                    <div className="shipform-input-group">
                      <label className="shipform-label">Costo base</label>
                      <input
                        type="number"
                        name="costo_base"
                        value={form.costo_base}
                        onChange={updateField}
                        className="shipform-input"
                        disabled={saving}
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                      />
                    </div>

                    <div className="shipform-input-group">
                      <label className="shipform-label">Entrega desde</label>
                      <input
                        type="number"
                        name="tiempo_entrega_desde"
                        value={form.tiempo_entrega_desde}
                        onChange={updateField}
                        className="shipform-input"
                        disabled={saving}
                        min="0"
                        step="1"
                        placeholder="0"
                      />
                    </div>

                    <div className="shipform-input-group">
                      <label className="shipform-label">Entrega hasta</label>
                      <input
                        type="number"
                        name="tiempo_entrega_hasta"
                        value={form.tiempo_entrega_hasta}
                        onChange={updateField}
                        className="shipform-input"
                        disabled={saving}
                        min="0"
                        step="1"
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div className="shipform-input-group">
                    <label className="shipform-label">Unidad de tiempo</label>
                    <select
                      name="unidad_tiempo"
                      value={form.unidad_tiempo}
                      onChange={updateField}
                      className="shipform-select"
                      disabled={saving}
                    >
                      <option value="DIAS">Días</option>
                      <option value="HORAS">Horas</option>
                    </select>
                  </div>
                </section>

                <aside className="shipform-side-card">
                  <h3 className="shipform-side-title">Vista operativa</h3>

                  <div className="shipform-kpi-card">
                    <span className="shipform-kpi-label">Costo base</span>
                    <strong className="shipform-kpi-value">{preview.costo}</strong>
                  </div>

                  <div className="shipform-kpi-card">
                    <span className="shipform-kpi-label">Entrega estimada</span>
                    <strong className="shipform-kpi-value">{preview.entrega}</strong>
                  </div>
                </aside>
              </div>
            )}

            {activeTab === TAB_KEYS.RULES && (
              <div className="shipform-panel-grid-single">
                <section className="shipform-section-card">
                  <div className="shipform-rules-header">
                    <div>
                      <h3 className="shipform-section-title">Reglas por zona</h3>
                      <p className="shipform-section-text">
                        Ajusta disponibilidad y costos específicos para cada
                        zona de envío.
                      </p>
                    </div>

                    <button
                      type="button"
                      className="shipform-secondary-btn"
                      onClick={() => onManageRules?.(metodoData)}
                      disabled={saving}
                    >
                      Administrar reglas
                    </button>
                  </div>

                  {!hasMethodId ? (
                    <div className="shipform-empty-state">
                      Guarda primero el método para comenzar a configurar sus
                      reglas por zona.
                    </div>
                  ) : rules.length === 0 ? (
                    <div className="shipform-empty-state">
                      Este método aún no tiene reglas configuradas.
                    </div>
                  ) : (
                    <div className="shipform-rules-grid">
                      {rules.map((rule) => {
                        const disponible =
                          Number(rule.disponible ?? rule.estado ?? 0) === 1;
                        const usaCostoBase =
                          Number(rule.usa_costo_base ?? 0) === 1;

                        return (
                          <div
                            key={rule.tienda_metodo_envio_regla_id}
                            className={`shipform-rule-card${
                              disponible ? "" : " is-muted"
                            }`}
                          >
                            <div className="shipform-rule-top">
                              <div>
                                <h4 className="shipform-rule-title">
                                  {rule.zona_nombre || rule.nombre_zona || "Zona"}
                                </h4>
                                <p className="shipform-rule-sub">
                                  {usaCostoBase
                                    ? "Costo base + recargo"
                                    : "Costo final definido"}
                                </p>
                              </div>

                              <span
                                className={`shipform-badge ${
                                  disponible ? "is-success" : "is-muted"
                                }`}
                              >
                                {disponible ? "Disponible" : "No disponible"}
                              </span>
                            </div>

                            <div className="shipform-rule-meta-grid">
                              <div className="shipform-rule-meta-item">
                                <span className="shipform-rule-meta-label">Recargo</span>
                                <strong className="shipform-rule-meta-value">
                                  {formatearPrecio(
                                    rule.recargo,
                                    configuracionTienda,
                                    { espacioEntreSimbolo: true }
                                  ) || "—"}
                                </strong>
                              </div>

                              <div className="shipform-rule-meta-item">
                                <span className="shipform-rule-meta-label">Costo final</span>
                                <strong className="shipform-rule-meta-value">
                                  {formatearPrecio(
                                    rule.costo_final,
                                    configuracionTienda,
                                    { espacioEntreSimbolo: true }
                                  ) || "—"}
                                </strong>
                              </div>

                              <div className="shipform-rule-meta-item">
                                <span className="shipform-rule-meta-label">Modo</span>
                                <strong className="shipform-rule-meta-value">
                                  {usaCostoBase ? "Base + recargo" : "Fijo"}
                                </strong>
                              </div>
                            </div>

                            {(onEditRule || onDeleteRule) && (
                              <div className="shipform-rule-card-actions">
                                {onEditRule && (
                                  <button
                                    type="button"
                                    className="shipform-rule-action-btn"
                                    onClick={() => onEditRule(rule)}
                                    disabled={saving}
                                  >
                                    Editar
                                  </button>
                                )}
                                {onDeleteRule && (
                                  <button
                                    type="button"
                                    className="shipform-rule-action-btn-danger"
                                    onClick={() => onDeleteRule(rule)}
                                    disabled={saving}
                                  >
                                    Eliminar
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              </div>
            )}

            {activeTab === TAB_KEYS.PREVIEW && (
              <div className="shipform-preview-wrap">
                <section className="shipform-preview-card">
                  <div className="shipform-preview-header">
                    <span
                      className={`shipform-badge ${
                        Number(form.estado) === 1 ? "is-success" : "is-muted"
                      }`}
                    >
                      {preview.estado}
                    </span>
                  </div>

                  <h3 className="shipform-preview-title">{preview.nombreVisible}</h3>
                  <p className="shipform-preview-desc">{preview.descripcion}</p>

                  <div className="shipform-preview-details">
                    <div className="shipform-preview-detail">
                      <span className="shipform-preview-detail-label">Costo</span>
                      <strong className="shipform-preview-detail-value">{preview.costo}</strong>
                    </div>

                    <div className="shipform-preview-detail">
                      <span className="shipform-preview-detail-label">Entrega</span>
                      <strong className="shipform-preview-detail-value">
                        {preview.entrega}
                      </strong>
                    </div>
                  </div>

                  {preview.requiereCuenta && (
                    <div className="shipform-preview-extra">
                      <h4 className="shipform-preview-extra-title">
                        Campos solicitados al cliente
                      </h4>
                      <div className="shipform-preview-field-pill-wrap">
                        <span className="shipform-preview-field-pill">
                          {preview.labelCuenta}
                        </span>
                        <span className="shipform-preview-field-pill">
                          {preview.labelEmpresa}
                        </span>
                      </div>
                    </div>
                  )}
                </section>
              </div>
            )}
          </div>

          <div className="shipform-footer">
            <button
              type="button"
              className="shipform-cancel-btn"
              onClick={onClose}
              disabled={saving}
            >
              Cancelar
            </button>

            <button
              type="submit"
              className={`shipform-save-btn${saving ? " is-disabled" : ""}`}
              disabled={saving}
            >
              {saving
                ? "Guardando..."
                : isEdit
                ? "Guardar cambios"
                : "Crear método"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ShippingFormModal;